import { useMemo, useState } from 'react'
import { api } from '@/api/client'
import { Icon } from '@/components/Icon'
import { useAsync } from '@/components/useAsync'
import { fmtBytes } from '@/lib/format'
import type { InstanceDetail } from '@/lib/scientific'

type WorkspaceFile = InstanceDetail['files'][number]
interface Folder { path: string; name: string; folders: Map<string, Folder>; files: WorkspaceFile[] }
const binaryFile = /\.(set|mat|edf|fif|psdata|pdf|png|jpe?g|gif|webp|zip|gz|xlsx?|parquet|hdf5?|npy|npz)$/i

function folders(files: WorkspaceFile[]): Folder {
  const root: Folder = { path: '', name: '', folders: new Map(), files: [] }
  for (const file of files) {
    const parts = file.path.split('/')
    let parent = root
    for (const name of parts.slice(0, -1)) {
      if (!parent.folders.has(name)) parent.folders.set(name, {
        path: [parent.path, name].filter(Boolean).join('/'), name, folders: new Map(), files: [],
      })
      parent = parent.folders.get(name)!
    }
    parent.files.push(file)
  }
  return root
}

/** The tree contains only the solver's inputs and guest runtime files, read from the Hub at the pinned commit. */
export function WorkspaceFiles({ base, files, revision }: { base: string; files: WorkspaceFile[]; revision?: string }) {
  const [path, setPath] = useState(files[0]?.path ?? '')
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState(new Set<string>())
  const [source, setSource] = useState(false)
  const query = search.trim().toLowerCase()
  const tree = useMemo(() => folders(files.filter(file => file.path.toLowerCase().includes(query))), [files, query])
  const file = files.find(file => file.path === path)
  const binary = binaryFile.test(path)
  const { data, error, loading, reload } = useAsync(() => path && !binary
    ? api.get<{ path: string; text: string; truncated: boolean; size: number }>(`${base}/file`, { path, ...(revision ? { revision } : {}) })
    : Promise.resolve(null), [base, path, binary, revision])
  const current = data?.path === path ? data : null
  const rows = useMemo(() => current && current.text && /\.csv$/i.test(path) ? csvRows(current.text) : null, [current, path])
  const previewLimit = [rows && !source && rows.length > 201 ? 'Table: first 200 rows.' : '',
    current?.truncated ? 'Preview: first 128 KiB. Download the file for its complete contents.' : ''].filter(Boolean).join(' ')
  function toggle(path: string) {
    setCollapsed(previous => { const next = new Set(previous); if (next.has(path)) next.delete(path); else next.add(path); return next })
  }
  function select(path: string) { setPath(path); setSource(false) }
  const [downloadError, setDownloadError] = useState('')
  async function download() {
    setDownloadError('')
    try {
      const file = await api.get<{ filename: string; url: string }>(`${base}/download`, { path, ...(revision ? { revision } : {}) })
      const link = document.createElement('a')
      link.href = file.url; link.download = file.filename; link.rel = 'noopener'; link.click()
    } catch (e) { setDownloadError((e as Error).message) }
  }
  if (!files.length) return <p className="muted small">No workspace files.</p>
  return <div className="science-file-browser">
    <div className="science-file-toolbar">
      <h2>Compiled task package <span className="muted small">{files.length} files</span></h2>
      <label className="list-search"><Icon name="search" size={16} /><input type="search" aria-label="Find a file" placeholder="Find a file…" value={search} onChange={event => { setSearch(event.target.value); setCollapsed(new Set()) }} /></label>
    </div>
    <div className="science-file-layout">
      <nav className="science-file-tree" aria-label="Workspace files">
        <FolderContents folder={tree} path={path} collapsed={collapsed} toggle={toggle} select={select} />
        {!tree.folders.size && !tree.files.length && <p className="muted small">No matching files.</p>}
      </nav>
      <section className="science-file-inspector" aria-label="File preview" aria-busy={loading}>
        <header className="science-file-preview-heading">
          <div><strong>{path.split('/').at(-1)}</strong><code>{path}</code></div>
          <span className="muted small" title={previewLimit || undefined}>{fmtBytes(file?.size || current?.size)}</span>
          <button className="sm science-file-download" onClick={() => void download()}><Icon name="download" size={14} />Download</button>
        </header>
        {downloadError && <p className="note err" role="alert">{downloadError}</p>}
        {binary ? <p className="muted small science-file-message">Preview unavailable for this file type. Download the file to open it.</p>
          : error ? <p className="note err" role="alert">{error} <button onClick={reload}>Try again</button></p>
          : !current ? <p className="muted small science-file-message" role="status">Loading file…</p>
          : <>
            {rows && <div className="science-file-views" role="group" aria-label="File view">
              <button aria-pressed={!source} onClick={() => setSource(false)}>Table</button>
              <button aria-pressed={source} onClick={() => setSource(true)}>Source</button>
            </div>}
            {rows && !source ? <div className="science-file-table" tabIndex={0} role="region" aria-label="CSV data">
              <table><thead><tr>{rows[0]?.map((cell, index) => <th key={index} scope="col">{cell}</th>)}</tr></thead>
                <tbody>{rows.slice(1, 201).map((row, index) => <tr key={index}>{row.map((cell, column) => <td key={column}>{cell}</td>)}</tr>)}</tbody>
              </table>
            </div> : <pre className="science-file-preview" tabIndex={0} aria-label="File source">{current.text || 'Empty file'}</pre>}
          </>}
      </section>
    </div>
  </div>
}

function FolderContents({ folder, path, collapsed, toggle, select }: {
  folder: Folder; path: string; collapsed: Set<string>
  toggle: (path: string) => void; select: (path: string) => void
}) {
  return <ul>
    {[...folder.folders.values()].sort((a, b) => a.name.localeCompare(b.name)).map(child => {
      const open = !collapsed.has(child.path)
      return <li key={child.path}>
        <button className="science-folder" aria-expanded={open} title={child.path} onClick={() => toggle(child.path)}>
          <Icon name="chevron" size={12} style={{ transform: open ? undefined : 'rotate(-90deg)' }} /><Icon name="folder" size={15} /><span>{child.name}</span>
        </button>
        {open && <FolderContents folder={child} path={path} collapsed={collapsed} toggle={toggle} select={select} />}
      </li>
    })}
    {folder.files.slice().sort((a, b) => a.path.localeCompare(b.path, undefined, { numeric: true })).map(file => <li key={file.path}>
      <button className="science-file" aria-label={file.path} aria-current={file.path === path ? 'true' : undefined} title={file.path} onClick={() => select(file.path)}>
        <Icon name="file" size={15} /><span>{file.path.split('/').at(-1)}</span>
      </button>
    </li>)}
  </ul>
}

/** Keep CSV values as text, including quoted commas, newlines and escaped quotes. */
function csvRows(text: string) {
  const rows: string[][] = []
  let row: string[] = [], cell = '', quoted = false, closed = false
  const value = text.replace(/^\uFEFF/, '')
  for (let i = 0; i < value.length; i++) {
    const char = value[i]
    if (closed && ![',', '\n', '\r'].includes(char)) return null
    if (char === '"') {
      if (quoted && value[i + 1] === '"') { cell += '"'; i++ }
      else if (quoted) { quoted = false; closed = true }
      else if (!cell) quoted = true
      else return null
    } else if (!quoted && char === ',') { row.push(cell); cell = ''; closed = false }
    else if (!quoted && (char === '\n' || char === '\r')) {
      row.push(cell); rows.push(row); row = []; cell = ''; closed = false
      if (char === '\r' && value[i + 1] === '\n') i++
    } else cell += char
  }
  if (quoted) return null
  if (cell || row.length || closed) { row.push(cell); rows.push(row) }
  return rows.every(row => row.length <= 50) ? rows : null
}
